import type { PricingCatalog, RegionalPricingTable } from "@reno-estimator/core";
import { allSamplePricingLineItems, SAMPLE_EFFECTIVE_DATE, SAMPLE_PRICING_VERSION } from "./sample-line-items.js";
import { allSampleV2Items, SAMPLE_EFFECTIVE_DATE_V2, SAMPLE_PRICING_VERSION_V2 } from "./sample-line-items-v2.js";
import {
  allSampleRegionalMultipliers,
  sampleCityMultipliers,
  samplePostalMultipliers,
  sampleProvinceMultipliers
} from "./sample-regional-multipliers.js";
import { createPricingCatalogArchive, publishCatalogVersion, type PricingCatalogArchive } from "../schema/versioning.js";

const SAMPLE_TIMESTAMP = "2026-08-24T00:00:00.000Z";
const SAMPLE_TIMESTAMP_V2 = "2026-09-01T00:00:00.000Z";

/**
 * Assembles the Phase 1 sample pricing catalog ("2026.08-sample-1"):
 * kitchen, bathroom, and basement line items, no sources (see
 * ./README.md), `isPlaceholder: true` because none of this data is
 * verified. Kept unchanged since Phase 1 so it stays a faithful "what
 * this version actually contained" historical snapshot for the
 * versioning system to preserve - see buildSamplePricingCatalogV2 below
 * for the current version.
 */
export function buildSamplePricingCatalog(): PricingCatalog {
  return {
    meta: {
      pricingVersion: SAMPLE_PRICING_VERSION,
      effectiveDate: SAMPLE_EFFECTIVE_DATE,
      createdAt: SAMPLE_TIMESTAMP,
      modifiedAt: SAMPLE_TIMESTAMP,
      notes:
        "Phase 1 initial sample dataset covering kitchen, bathroom, and basement project types. All figures are administrator supplied sample values pending real sourced Canadian pricing data.",
      status: "inactive",
      isPlaceholder: true
    },
    items: allSamplePricingLineItems,
    sources: []
  };
}

/**
 * Assembles the Phase 2 sample pricing catalog ("2026.09-sample-2"):
 * everything in the Phase 1 catalog plus accessibility and
 * permit/design-allowance line items across kitchen/bathroom/basement
 * (Phase 2), the fuller kitchen tiered line-item set (Phase 5A), the
 * fuller basement tiered line-item set (Phase 8), and the whole-home
 * renovation line-item set (Phase 10) - needed by the corresponding
 * calculation engine rules (see packages/core/src/calculation/rules/).
 * Still entirely administrator supplied sample values -
 * `isPlaceholder: true`. This is the version the calculation engine's
 * tests and examples use as "the current catalog," and stays the SAME
 * pricing version across those phases (rather than bumping to v3/v4/etc
 * per phase) since each just adds new line items for a graduating
 * project type without changing any figure already shipped - see
 * sample-line-items-v2.ts's per-phase item groups for the itemized
 * history.
 */
export function buildSamplePricingCatalogV2(): PricingCatalog {
  return {
    meta: {
      pricingVersion: SAMPLE_PRICING_VERSION_V2,
      effectiveDate: SAMPLE_EFFECTIVE_DATE_V2,
      createdAt: SAMPLE_TIMESTAMP_V2,
      modifiedAt: SAMPLE_TIMESTAMP_V2,
      notes:
        "Phase 2: adds accessibility and permit/design allowance line items across kitchen, bathroom, and basement. Later phases (still under this same version) add the fuller kitchen (5A), basement (8), and whole-home renovation (10) line-item sets. Still entirely administrator supplied sample values.",
      status: "active",
      isPlaceholder: true
    },
    items: allSampleV2Items,
    sources: []
  };
}

/**
 * Convenience factory: an archive with both sample versions published -
 * v1 first (then demoted to inactive), v2 published second and left
 * active. Exists mainly for tests/examples that want to demonstrate the
 * versioning system and "pricing version changed" behavior against real
 * (if sample) data without hand-assembling the archive every time.
 */
export function buildSampleCatalogArchive(): PricingCatalogArchive {
  let archive = createPricingCatalogArchive();
  archive = publishCatalogVersion(archive, buildSamplePricingCatalog());
  archive = publishCatalogVersion(archive, buildSamplePricingCatalogV2());
  return archive;
}

/**
 * Assembles the Phase 1 sample regional pricing table: all 13
 * provinces/territories, plus sparse illustrative city/postal entries.
 * `isPlaceholder: true` for the same reason as the pricing catalog.
 */
export function buildSampleRegionalPricingTable(): RegionalPricingTable {
  return {
    meta: {
      version: SAMPLE_PRICING_VERSION,
      effectiveDate: SAMPLE_EFFECTIVE_DATE,
      createdAt: SAMPLE_TIMESTAMP,
      modifiedAt: SAMPLE_TIMESTAMP,
      isPlaceholder: true
    },
    nationalBaselineMultiplier: 1.0,
    provinceEntries: sampleProvinceMultipliers,
    cityEntries: sampleCityMultipliers,
    postalEntries: samplePostalMultipliers
  };
}

export { allSamplePricingLineItems, allSampleRegionalMultipliers, allSampleV2Items };
